// hooks/useStops.ts
"use client";

import { useState, useRef, useCallback } from "react";
import { fetchGeocode } from "@/services/geocodeService";
import { Coordinates } from "@/types/distance.types";
import { Stops } from "@/types/trip.types";
import { showPopup } from "@/components/warnings/Popup";

export function useStops(initialStops: Stops = [
  { address: "", coords: {latitude:0, longitude:0} },
  { address: "", coords: {latitude:0, longitude:0} },
]) {
  const [stops, setStops] = useState<Stops>(initialStops);
  const [shouldRecalculate, setShouldRecalculate] = useState(false);
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);

  // --- Update a stop (address or coordinates)
  const updateStop = useCallback(
    async (index: number, address: string, coords?: Coordinates) => {
      setStops(prev => {
        const newStops = [...prev];
        newStops[index] = { ...newStops[index], address };
        return newStops;
      });

      // If coords were passed (from autocomplete or drag), skip geocode
      if (coords) {
        setStops(prev => {
          const newStops = [...prev];
          newStops[index] = { address, coords };
          return newStops;
        });
        setShouldRecalculate(true);
        return;
      }

      // Otherwise debounce geocoding to avoid API spam
      if (debounceTimeout.current) clearTimeout(debounceTimeout.current);

      debounceTimeout.current = setTimeout(async () => {
        if (!address) {
          setStops(prev => {
            const newStops = [...prev];
            newStops[index] = { ...newStops[index], coords: {latitude:0, longitude:0} as Coordinates };
            return newStops;
          });
          return;
        }

        try {
          const result = await fetchGeocode(address);

          if (!result) return null;

          const coords = result.coordinates;
          setStops(prev => {
            const newStops = [...prev];
            newStops[index] = { address, coords };
            return newStops;
          });
          setShouldRecalculate(true);
        } catch {
          showPopup(
            "Unexpected Error",
            "An unexpected error has occured."
          )
        }
      }, 800);
    },
    []
  );

  // --- Add a stop before the last one
  const addStop = () => {
    const middleIndex = stops.length - 1;
    const newStops = [...stops];
    newStops.splice(middleIndex, 0, { address: "", coords: {latitude:0, longitude:0} as Coordinates });
    setStops(newStops);
  };

  // --- Remove a stop
  const removeStop = (index: number) => {
    if (stops.length <= 2) {
      alert("Start and End point required.");
      return;
    }
    setStops(prev => prev.filter((_, i) => i !== index));
    setShouldRecalculate(true);
  };

  // --- Clear one stop
  const clearStop = (index: number) => {
    setStops(prev => {
      const newStops = [...prev];
      newStops[index] = { address: "", coords: {latitude:0, longitude:0} as Coordinates };
      return newStops;
    });
    setShouldRecalculate(true);
  };

  // --- Reorder stops
  const reorderStops = (oldIndex: number, newIndex: number) => {
    const updated = [...stops];
    const [moved] = updated.splice(oldIndex, 1);
    updated.splice(newIndex, 0, moved);
    setStops(updated);

    const allValid = updated.every(stop => stop.coords.latitude !== 0 && stop.coords.longitude !== 0);
    if (allValid) setShouldRecalculate(true);
  };

  // --- Reset all stops
  const clearTripInputs = () => {
    setStops([
      { address: "", coords: {latitude:0, longitude:0} as Coordinates },
      { address: "", coords: {latitude:0, longitude:0} as Coordinates},
    ]);
    setShouldRecalculate(false);
  };

  return {
    stops,
    setStops,
    shouldRecalculate,
    setShouldRecalculate,
    updateStop,
    addStop,
    removeStop,
    clearStop,
    reorderStops,
    clearTripInputs
  };
}
